import type { AxiosInstance } from "axios";
import { z } from "zod";

import { Roles } from "api/schemas/shared";
import { getRolePathPrefix } from "api/services/utils";
import { validateResponse } from "api/validateResponse";

import type { GetTreatmentSchedulesParams } from "./hooks/useGetTreatmentSchedules";
import { type GetScheduleWeekArgs, ScheduleWeekSchema } from "./schemas/ScheduleWeek";
import {
  ConvertTreatmentTemplateResponseSchema,
  PatientTreatmentScheduleSchema,
  TreatmentScheduleGroupsResponseSchema,
  TreatmentScheduleTemplateSchema,
  TreatmentSchedulesResponseSchema,
} from "./schemas/Treatment";
import type {
  ConvertTreatmentTemplatePayload,
  PostTreatmentSchedulePayload,
  PutPatientTreatmentScheduleActivityArgs,
  PutPatientTreatmentScheduleArgs,
  PutTreatmentScheduleArgs,
  UpdatePatientTreatmentScheduleActivityArgs,
} from "./schemas/Treatment";

export const createTreatmentService = (transportV2: AxiosInstance) => ({
  // PATIENT TREATMENT SCHEDULES

  async postPatientTreatmentStart(patientID: number) {
    const response = await transportV2.post(`/patients/${patientID}/treatment/start`);
    return validateResponse(response, z.any(), false);
  },

  async getPatientTreatmentSchedule(patientID: number) {
    const response = await transportV2.get(`/therapists/patients/${patientID}/treatment/schedule`);
    return validateResponse(response, PatientTreatmentScheduleSchema, false);
  },

  async putPatientTreatmentSchedule(args: PutPatientTreatmentScheduleArgs) {
    const response = await transportV2.put(`/therapists/patients/${args.patientID}/treatment/schedule`, args.payload);
    return validateResponse(response, PatientTreatmentScheduleSchema, false);
  },

  async putPatientTreatmentScheduleActivity(args: PutPatientTreatmentScheduleActivityArgs) {
    const response = await transportV2.put(
      `/therapists/patients/${args.patientID}/treatment/schedule/activities/${args.userActivityScheduleID}`,
      args.payload
    );
    return validateResponse(response, z.any(), false);
  },

  async deletePatientTreatmentScheduleActivity(args: UpdatePatientTreatmentScheduleActivityArgs) {
    const response = await transportV2.delete(
      `/therapists/patients/${args.patientID}/treatment/schedule/activities/${args.userActivityScheduleID}`
    );
    return validateResponse(response, z.any(), false);
  },

  async getPatientTreatmentScheduleWeek(args: GetScheduleWeekArgs) {
    const { locale, patient_id, treatment_id, week, role = Roles.Therapist } = args;
    const response = await transportV2.get(
      `${getRolePathPrefix(role)}/patients/${patient_id}/treatments/${treatment_id}/schedule/weeks/${week}`,
      {
        params: { locale },
      }
    );
    return validateResponse(response, ScheduleWeekSchema, false);
  },

  // TREATMENT SCHEDULE TEMPLATES

  async postConvertTreatmentTemplate(args: ConvertTreatmentTemplatePayload) {
    const response = await transportV2.post("/therapists/treatment_templates/convert", args);
    return validateResponse(response, ConvertTreatmentTemplateResponseSchema, false);
  },

  async postTreatmentSchedule(args: PostTreatmentSchedulePayload) {
    const response = await transportV2.post("/therapists/treatments/schedule", args);
    return validateResponse(response, TreatmentScheduleTemplateSchema, false);
  },

  async getTreatmentSchedules(params: GetTreatmentSchedulesParams) {
    const response = await transportV2.get("/therapists/treatments/schedules", { params });
    return validateResponse(response, TreatmentSchedulesResponseSchema, false);
  },

  async getTreatmentScheduleGroups() {
    const response = await transportV2.get("/therapists/treatments/schedules/groups");
    return validateResponse(response, TreatmentScheduleGroupsResponseSchema, false);
  },

  async getTreatmentSchedule(treatmentScheduleID: number) {
    const response = await transportV2.get(`/therapists/treatments/schedules/${treatmentScheduleID}`);
    return validateResponse(response, TreatmentScheduleTemplateSchema, false);
  },

  async putTreatmentSchedule(args: PutTreatmentScheduleArgs) {
    const response = await transportV2.put(
      `/therapists/treatments/schedules/${args.treatmentScheduleID}`,
      args.payload
    );
    return validateResponse(response, TreatmentScheduleTemplateSchema, false);
  },

  async deleteTreatmentSchedule(treatmentScheduleID: number) {
    const response = await transportV2.delete(`/therapists/treatments/schedules/${treatmentScheduleID}`);
    return validateResponse(response, z.any(), false);
  },
});

export type TreatmentService = ReturnType<typeof createTreatmentService>;
